import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../Sidebar";
import { useLocation } from "react-router-dom";
import { FaRankingStar } from "react-icons/fa6";
import { LayoutDashboard, Hourglass } from "lucide-react";
import { getDB, dbGet, dbPut } from "../../db";

import ChurnedSuppliersTable from "./ChurnedSuppliersTable";
import InactiveSuppliers from "./InactiveSuppliers";
import Top10PurchaseTransactions from "./Top10PurchaseTransactions";
import Top10NegativePurchaseTransactions from "./Top10NegativePurchaseTransactions";
import OutlierPurchaseTransactions from "./OutlierPurchaseTransactions";
import LargestSmallestPurchaseTransactions from "./LargestSmallestPurchaseTransactions";



import "../sprzedaz/DashboardGrid.css";
import TopBar from "../TopBar";

const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "8px",
  padding: "20px 20px 40px 20px",
  backgroundColor: "#f9f9f9",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
};

const pillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 10px",
  borderRadius: 999,
  fontSize: "0.7rem",
  fontWeight: 600,
  backgroundColor: "#f3e5ff",
  color: "#7a3ea9",
};

const cardTitleStyle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "#222",
  marginBottom: 6,
};

const cardTextStyle = {
  fontSize: "0.85rem",
  color: "#555",
  lineHeight: 1.5,
};

// SAME IDEA AS IN SprzedazCreative, BUT WITH ZAKUPY COLORS
const SectionTitle = ({ title, inGrid = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 20px 12px 20px",
      margin: inGrid ? "8px 0 12px 0" : "8px 20px 12px 20px",
      fontSize: "1.05rem",
      fontWeight: 700,
      color: "#2b2b2b",
      position: "relative",
      background:
        "linear-gradient(to right, rgba(152,72,199,0.12), rgba(152,72,199,0.04))",
      border: "1px solid rgba(152,72,199,0.35)",
      borderRadius: "8px",
      ...(inGrid
        ? { gridColumn: "1 / -1", paddingLeft: "20px", paddingRight: "20px" }
        : {}),
    }}
  >
    <LayoutDashboard size={20} strokeWidth={2} color="#9848c7" />
    <span style={{ position: "relative", display: "inline-block" }}>
      {title}
      <span
        style={{
          position: "absolute",
          left: 0,
          bottom: -3,
          width: "100%",
          height: 2,
          backgroundColor: "#9848c7",
          borderRadius: 2,
          opacity: 0.7,
        }}
      />
    </span>
  </div>
);

const formatPath = (path) => {
  const customNames = {
    "zakupy/creative": {
      name: "Zakupów / Raporty Kreatywne",
      icon: <FaRankingStar />,
    },
  };
  const cleanPath = path.replace(/^\//, "");
  return (
    customNames[cleanPath] || {
      name: cleanPath.replace("/", " / "),
      icon: null,
    }
  );
};

const ZakupyCreative = () => {
  const [zakupyData, setZakupyData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const locationData = useLocation();
  const { name, icon } = formatPath(locationData.pathname);

  useEffect(() => {
    const fetchZakupyData = async () => {
      try {
        await getDB();
        const cached = await dbGet("zakupy", "allZakupy");
        if (cached?.data) setZakupyData(cached.data);
      } catch (e) {
        console.error("fetchZakupyData error:", e);
      }
    };

    const fetchClientsData = async () => {
      try {
        await getDB();
        const now = Date.now();
        const cached = await dbGet("clients", "allClients");
        if (cached && now - cached.timestamp < 60 * 60 * 1000) {
          setClientsData(cached.data);
          return;
        }
        const response = await axios.get(
          "https://server229256.nazwa.pl/api/klienci"
        );
        await dbPut("clients", "allClients", {
          data: response.data,
          timestamp: now,
        });
        setClientsData(response.data);
      } catch (err) {
        console.error("Error fetching clients:", err);
      }
    };

    fetchZakupyData();
    fetchClientsData();
  }, []);

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <TopBar />

        {/* HEADER */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 20px",
            fontSize: "1.2rem",
            fontWeight: "bold",
            color: "#9848c7",
            marginBottom: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {icon} {name}
          </div>
        </div>




        {/* CONTENT: 3 SECTIONS, 2 CARDS EACH (JAK SPRZEDAZ CREATIVE) */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            padding: "20px",
            backgroundColor: "#f5f5f5",
          }}
        >
          {/* SEKCJA 1 */}
          <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
            <SectionTitle
              inGrid
              title="Stali dostawcy, od których przestaliśmy kupować, oraz dostawcy, od których nie kupiliśmy od 6 miesięcy"
            />

            {/* Card 1: Stali dostawcy, od których przestaliśmy kupować */}
            <div className="card-span-6" style={cardStyle}>
<ChurnedSuppliersTable zakupyData={zakupyData} />
            </div>

            <div className="card-span-6" style={cardStyle}>
 <InactiveSuppliers zakupyData={zakupyData} />
            </div>

          </div>

          {/* SEKCJA 2 */}
          <SectionTitle
            inGrid
            title="Analiza transakcji – Top 10 transactions and Top 10 corrections"
          />
          <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
            {/* Card 3: Top 10 transakcji */}
            <div className="card-span-6" style={cardStyle}>
  <Top10PurchaseTransactions zakupyData={zakupyData} />
</div>

<div className="card-span-6" style={cardStyle}>
  <Top10NegativePurchaseTransactions zakupyData={zakupyData} />
</div>
          </div>

          {/* SEKCJA 3 */}
          <SectionTitle
            inGrid
            title="Transakcje poza normą, Największe i najmniejsze transakcje"
          />
          <div className="dashboard-grid">
            {/* Card 5: Transakcje poza normą */}
<div className="card-span-6" style={cardStyle}>
  <OutlierPurchaseTransactions zakupyData={zakupyData} />
</div>

  <div className="card-span-6" style={cardStyle}>
    <LargestSmallestPurchaseTransactions zakupyData={zakupyData} />
  </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZakupyCreative;
